import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { TERRAZZO_COLORS } from "@/lib/catalog";

const HEX = {
  onyx_classic: "#1a1a1a", snowfall: "#f5f2eb", salt_and_pepper: "#b8b3a8",
  ash: "#8a8580", charcoal_frost: "#4a4a48", ivory_pebble: "#e8ddc8",
  bone_and_bark: "#c9b896", terra_rossa: "#a04525", brick_lane: "#a04a35",
  ruby_ash: "#7a2a2a", rosewood: "#654030", cocoa_cream: "#b8956e",
  blush_stone: "#e8b8a8", meadow: "#7a9060", olive_grove: "#6a7040",
  deep_forest: "#2a4028", emerald_glass: "#2a7050", fern_light: "#a8c090",
  red_cedar: "#8a3a2a", scarlet_noir: "#501818", verde_nero: "#1a3020",
  recycled_green: "#5a8a5a", sea_glass: "#a0c8b8", amber_woods: "#906030",
  pepper_field: "#504040",
  celestial_white: "#ede8d8", midnight_quartz: "#101018", rose_marble: "#d8a898",
  champagne_toast: "#d8b878", smoked_pearl: "#b8a89c", amber_ember: "#b8632a",
  forest_jewel: "#204830", onyx_rose: "#38181c", golden_sand: "#c89858",
  storm_grey: "#58585c", ruby_noir: "#4a1015", sage_luxe: "#90a080",
  cocoa_gold: "#856340", pink_salt: "#f0c8b8", emerald_onyx: "#143a2a",
};

const Swatch = ({ c }) => (
  <div className="group relative rise">
    <div
      className="rounded-md border border-[#D8D8D5] shadow-sm transition-transform group-hover:scale-[1.02]"
      style={{ backgroundColor: HEX[c.key] || "#CCC", aspectRatio: "1 / 1" }}
    />
    <div className="mt-2">
      <div className="text-sm font-medium text-[#1C1C1A] leading-tight">{c.label}</div>
      <div className="text-[10px] tracking-wider text-[#6E6E6A]">{(HEX[c.key] || "").toUpperCase()}</div>
    </div>
  </div>
);

export default function Catalog() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/catalog/colorcard.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "sango_color_card.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Color card downloaded");
    } catch { toast.error("Download failed"); }
    finally { setBusy(false); }
  };

  const standard = TERRAZZO_COLORS.filter(c => c.collection === "standard");
  const signature = TERRAZZO_COLORS.filter(c => c.collection === "signature");

  return (
    <div className="p-4 sm:p-8 grain-bg">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Sales asset</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-black">Color Card Catalog</h1>
            <p className="text-[#6E6E6A] text-sm mt-2 max-w-xl">
              All 40 Sango Terrazzo colors, organised into the Standard and Signature collections. Share the PDF on WhatsApp with any customer inquiry.
            </p>
          </div>
          <Button onClick={download} disabled={busy} data-testid="download-colorcard" className="bg-[#1C1C1A] hover:bg-black text-white gap-2 h-11">
            <Download className="w-4 h-4" /> {busy ? "Preparing…" : "Download PDF"}
          </Button>
        </div>

        <section className="mt-10">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">01</div>
              <h2 className="font-display text-2xl font-black tracking-tight">Standard Collection</h2>
            </div>
            <div className="text-xs text-[#6E6E6A]">{standard.length} colors</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {standard.map((c) => <Swatch key={c.key} c={c} />)}
          </div>
        </section>

        <section className="mt-12 pb-8">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#C85A42] flex items-center gap-2"><Sparkles className="w-3 h-3" /> 02 · Signature</div>
              <h2 className="font-display text-2xl font-black tracking-tight">Signature Collection</h2>
            </div>
            <div className="text-xs text-[#6E6E6A]">{signature.length} colors</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {signature.map((c) => <Swatch key={c.key} c={c} />)}
          </div>
          <p className="text-xs text-[#6E6E6A] mt-8 italic">On-screen colors are approximate. Please request a physical sample for exact tone.</p>
        </section>
      </div>
    </div>
  );
}
